<template>
  <div class="game-slot-symbols-editor">
    <label v-if="configs.label" class="block mb-2">{{ t(configs.label) }}</label>

    <div class="symbols-list">
      <div
        v-for="(symbol, index) in symbols"
        :key="index"
        class="symbol-item"
      >
        <div class="symbol-item-header">
          <i class="fas fa-grip-vertical drag-handle" />
          <span class="symbol-number">{{ index + 1 }}</span>
        </div>

        <div class="symbol-fields">
          <!-- Row 1: Type selector + content -->
          <div class="field-row">
            <div class="field-group field-type">
              <label>{{ t('templateBuilder.games.symbolType') }}</label>
              <Dropdown
                v-model="symbol.type"
                :options="typeOptions"
                option-label="label"
                option-value="value"
                class="w-full"
                @change="onSymbolChange"
              />
            </div>

            <!-- Image picker -->
            <div v-if="symbol.type === 'image'" class="field-group field-image-input">
              <label>{{ t('templateBuilder.games.image') }}</label>
              <div class="image-picker-wrapper">
                <Button
                  v-if="!symbol.image"
                  class="image-button image-button-empty"
                  icon="far fa-image"
                  @click="openImagePicker(index)"
                />
                <div
                  v-else
                  class="image-thumbnail-wrapper"
                  @click="openImagePicker(index)"
                >
                  <img :src="symbol.image" class="image-thumbnail" />
                  <Button
                    class="image-remove-btn"
                    icon="far fa-times"
                    @click.stop="clearImage(index)"
                  />
                </div>
              </div>
            </div>

            <!-- Text input + emoji picker -->
            <div v-if="symbol.type === 'text'" class="field-group field-text-input">
              <label>{{ t('templateBuilder.games.symbolText') }}</label>
              <div class="text-emoji-wrapper">
                <InputText
                  v-model="symbol.text"
                  class="w-full"
                  :placeholder="t('templateBuilder.games.symbolTextPlaceholder')"
                  @update:model-value="onSymbolChange"
                />
                <EmojisButton @on-choose-emoji="(emoji) => onEmojiInsert(index, emoji)" />
              </div>
            </div>

            <!-- Font size: auto/manual -->
            <div v-if="symbol.type === 'text'" class="field-group field-font-size">
              <label>{{ t('templateBuilder.games.fontSize') }}</label>
              <div class="font-size-wrapper">
                <Button
                  v-if="symbol.fontSize == null"
                  :class="['font-size-toggle', 'active']"
                  :label="t('templateBuilder.games.fontSizeAuto')"
                  class="p-button-sm"
                  @click="symbol.fontSize = 20; onSymbolChange()"
                />
                <template v-else>
                  <InputNumber
                    v-model="symbol.fontSize"
                    :min="8"
                    :max="80"
                    suffix="px"
                    class="font-size-input"
                    @update:model-value="onSymbolChange"
                  />
                  <Button
                    icon="far fa-undo"
                    class="p-button-sm p-button-text font-size-manual-btn"
                    v-tooltip.top="t('templateBuilder.games.fontSizeAuto')"
                    @click="symbol.fontSize = null; onSymbolChange()"
                  />
                </template>
              </div>
            </div>

            <div class="field-actions">
              <Button
                icon="far fa-trash-alt"
                class="p-button-text p-button-danger p-button-sm"
                :disabled="symbols.length <= 2"
                @click="removeSymbol(index)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="symbols-footer">
      <span class="symbols-count">{{ symbols.length }} {{ t('templateBuilder.games.symbolsCount') }}</span>
      <Button
        :label="t('templateBuilder.games.addSymbol')"
        icon="far fa-plus"
        class="p-button-secondary p-button-sm"
        @click="addSymbol"
      />
    </div>

    <!-- File manager dialog -->
    <FileManager
      v-if="showFileManager"
      :display-dialog="showFileManager"
      extensions="image/*"
      @on-close-dialog="showFileManager = false"
      @on-choose-file="onFileChosen"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  watch,
  onMounted,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Dropdown from 'primevue/dropdown';
import Button from 'primevue/button';
import EmojisButton from '@/components/fields/partials/EmojisButton.vue';
import FileManager from '@/components/file-manager/FileManager.vue';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
} from '@/types';

interface SlotSymbol {
  id: string;
  type: 'image' | 'text';
  image: string;
  text: string;
  label: string;
  color: string;
  backgroundColor: string;
  fontSize: number | null; // null = auto
}

export default defineComponent({
  name: 'GameSlotSymbolsEditor',

  components: {
    InputText,
    InputNumber,
    Dropdown,
    Button,
    EmojisButton,
    FileManager,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);
    const symbols = ref<SlotSymbol[]>([]);

    const showFileManager = ref(false);
    const imagePickerIndex = ref<number>(-1);

    const typeOptions = [
      { label: t('templateBuilder.games.text'), value: 'text' },
      { label: 'Image', value: 'image' },
    ];

    const defaultEmojis = ['🍒', '🍋', '🍊', '🍇', '⭐', '💎', '7️⃣', '🔔'];

    const detectType = (item: any): 'image' | 'text' => {
      if (item.type === 'image') return 'image';
      const img = item.image || '';
      if (img.match(/^(https?:\/\/|\/)/)) return 'image';
      return 'text';
    };

    const parseInitialValue = () => {
      try {
        const value = property.value.value;
        if (value && typeof value === 'string') {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            symbols.value = parsed.map((item: any, idx: number) => {
              const type = detectType(item);
              return {
                id: item.id || `symbol_${idx}`,
                type,
                image: type === 'image' ? (item.image || '') : '',
                text: type === 'text' ? (item.text || item.image || '') : '',
                label: item.label || '',
                color: item.color || '#333333',
                backgroundColor: item.backgroundColor || '',
                fontSize: item.fontSize != null ? Number(item.fontSize) : null,
              };
            });
            return;
          }
        }
      } catch (e) {
        // Ignore
      }

      // Default symbols
      symbols.value = [
        { id: 'cherry', type: 'text', image: '', text: '🍒', label: 'Cerise', color: '#333333', backgroundColor: '', fontSize: null },
        { id: 'lemon', type: 'text', image: '', text: '🍋', label: 'Citron', color: '#333333', backgroundColor: '', fontSize: null },
        { id: 'orange', type: 'text', image: '', text: '🍊', label: 'Orange', color: '#333333', backgroundColor: '', fontSize: null },
        { id: 'seven', type: 'text', image: '', text: '7️⃣', label: 'Sept', color: '#333333', backgroundColor: '', fontSize: null },
      ];
    };

    const emitChange = () => {
      // Build output: put display value in 'image' for backward compat
      const output = symbols.value.map((s, idx) => {
        const base: Record<string, any> = {
          id: s.id || `symbol_${idx}`,
          type: s.type,
          label: s.type === 'text' ? (s.text || '') : s.id,
          image: s.type === 'image' ? s.image : (s.text || ''),
        };
        if (s.type === 'text') {
          base.text = s.text || '';
          base.color = s.color || '#333333';
          if (s.fontSize != null) {
            base.fontSize = s.fontSize;
          }
        }
        if (s.backgroundColor) {
          base.backgroundColor = s.backgroundColor;
        }
        return base;
      });
      const jsonValue = JSON.stringify(output);
      property.value.value = jsonValue;
      emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
    };

    const onSymbolChange = () => {
      emitChange();
    };

    const onEmojiInsert = (index: number, emoji: string) => {
      symbols.value[index].text = (symbols.value[index].text || '') + emoji;
      emitChange();
    };

    const openImagePicker = (index: number) => {
      imagePickerIndex.value = index;
      showFileManager.value = true;
    };

    const onFileChosen = (file: any) => {
      const url = file?.url || file?.fullPath || file;
      if (url && imagePickerIndex.value >= 0) {
        symbols.value[imagePickerIndex.value].image = url;
        emitChange();
      }
      showFileManager.value = false;
    };

    const clearImage = (index: number) => {
      symbols.value[index].image = '';
      emitChange();
    };

    const addSymbol = () => {
      const emojiIndex = symbols.value.length % defaultEmojis.length;
      const newId = `symbol_${Date.now()}`;

      symbols.value.push({
        id: newId,
        type: 'text',
        image: '',
        text: defaultEmojis[emojiIndex],
        label: '',
        color: '#333333',
        backgroundColor: '',
        fontSize: null,
      });
      emitChange();
    };

    const removeSymbol = (index: number) => {
      if (symbols.value.length > 2) {
        symbols.value.splice(index, 1);
        emitChange();
      }
    };

    onMounted(() => {
      parseInitialValue();
    });

    watch(() => props.parserValues.properties[0]?.value, () => {
      parseInitialValue();
    }, { deep: true });

    return {
      t,
      symbols,
      typeOptions,
      showFileManager,
      onSymbolChange,
      onEmojiInsert,
      openImagePicker,
      onFileChosen,
      clearImage,
      addSymbol,
      removeSymbol,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/scss/utilities/_variables.scss';

.game-slot-symbols-editor {
  width: 100%;

  .symbols-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  .symbol-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem;
    background: $white;
    border: 1px solid $solitude;
    border-radius: 6px;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: $brand-color-primary;
    }
  }

  .symbol-item-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    padding-top: 1.5rem;

    .drag-handle {
      color: $mid-grey;
      cursor: grab;
      font-size: 0.875rem;

      &:active {
        cursor: grabbing;
      }
    }

    .symbol-number {
      font-size: 0.75rem;
      color: $mid-grey;
      font-weight: 600;
    }
  }

  .symbol-fields {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .field-row {
    display: flex;
    gap: 0.5rem;
    align-items: flex-end;
  }

  .field-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    label {
      font-size: 0.7rem;
      color: $mid-grey;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }

    &.field-type {
      flex: 0 0 80px;
    }

    &.field-image-input {
      flex: 0 0 50px;
    }

    &.field-text-input {
      flex: 1;
      min-width: 80px;
    }

    &.field-font-size {
      flex: 0 0 72px;
    }
  }

  .font-size-wrapper {
    display: flex;
    align-items: center;
    gap: 0.15rem;
    height: 36px;
  }

  .font-size-toggle {
    font-size: 0.7rem;
    padding: 0.3rem 0.4rem;
    border: 1px solid $solitude;
    background: transparent;
    color: $mid-grey;
    cursor: pointer;
    white-space: nowrap;
    width: 100%;

    &.active {
      background: $brand-color-primary;
      color: $white;
      border-color: $brand-color-primary;
    }
  }

  .font-size-input {
    width: 52px;
    flex-shrink: 0;

    :deep(.p-inputnumber-input) {
      padding: 0.3rem 0.15rem;
      text-align: center;
      font-size: 0.75rem;
      width: 100%;
    }
  }

  .font-size-manual-btn {
    padding: 0;
    width: 20px;
    height: 20px;
    flex-shrink: 0;

    :deep(.p-button-icon) {
      font-size: 0.65rem;
    }
  }

  .text-emoji-wrapper {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .image-picker-wrapper {
    display: flex;
    align-items: center;
  }

  .image-button-empty {
    width: 36px;
    height: 36px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed $mid-grey;
    background: transparent;
    color: $mid-grey;
    border-radius: 6px;
    cursor: pointer;

    &:hover {
      border-color: $brand-color-primary;
      color: $brand-color-primary;
    }
  }

  .image-thumbnail-wrapper {
    position: relative;
    width: 36px;
    height: 36px;
    cursor: pointer;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid $solitude;

    .image-thumbnail {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .image-remove-btn {
      position: absolute;
      top: -4px;
      right: -4px;
      width: 16px;
      height: 16px;
      padding: 0;
      font-size: 0.6rem;
      border-radius: 50%;
      background: #e74c3c;
      color: white;
      border: none;
      cursor: pointer;
    }
  }

  .field-actions {
    display: flex;
    align-items: flex-end;
    padding-bottom: 0.25rem;
  }

  .symbols-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 0.5rem;
    border-top: 1px solid $solitude;
  }

  .symbols-count {
    font-size: 0.875rem;
    color: $mid-grey;
  }
}
</style>
